/**
 * @fileoverview Runtime persistence abstraction for the renderer.
 *
 * The renderer no longer talks to Electron directly. Instead it depends on the
 * {@link PersistenceClient} interface, which is implemented by:
 *  - the Electron bridge (`window.investAlbum`) when running in the desktop app, and
 *  - a web client (localStorage today, cloud/Supabase later) when running in a browser.
 *
 * The active client is selected once at module load by {@link createPersistenceClient}.
 */

import { buildDefaultState, normalizeState } from '../../../shared/persistence'
import type { InvestAlbumApi, PersistedState } from '../../../shared/persistence'
import { openExternalInBrowser } from './externalLink'
import { isSupabaseConfigured } from './supabase'
import { createCloudClient } from './cloudPersistence'

/** Storage key used by the web client as its primary local store. */
const WEB_STATE_KEY = 'investalbum-web-state-v1'

/**
 * Runtime-agnostic persistence contract consumed by the renderer.
 * Mirrors the Electron bridge API but is also implementable in the browser.
 */
export interface PersistenceClient {
  /** Short runtime label, e.g. 'win32', 'darwin', or 'web'. */
  readonly platform: string
  /** Whether this runtime persists to the cloud (vs local-only). */
  readonly isCloud: boolean
  /** Load the full persisted state, or a default state when none exists. */
  loadState: () => Promise<PersistedState>
  /** Asynchronously persist the full app state. */
  saveState: (state: PersistedState) => Promise<void>
  /** Best-effort synchronous persist for `beforeunload`. */
  saveStateSync: (state: PersistedState) => void
  /** Open an allowed external URL. Resolves true when dispatched. */
  openExternalLink: (url: string) => Promise<boolean>
}

declare global {
  interface Window {
    investAlbum?: InvestAlbumApi
  }
}

/** Wrap the Electron preload bridge as a {@link PersistenceClient}. */
function createElectronClient(api: InvestAlbumApi): PersistenceClient {
  return {
    platform: api.platform,
    isCloud: false,
    loadState: () => api.loadState(),
    saveState: (state) => api.saveState(state),
    saveStateSync: (state) => {
      api.saveStateSync(state)
    },
    openExternalLink: (url) => api.openExternalLink(url)
  }
}

/** Browser persistence client backed by localStorage. */
function createWebClient(): PersistenceClient {
  function read(): PersistedState {
    try {
      const raw = window.localStorage.getItem(WEB_STATE_KEY)
      if (!raw) return buildDefaultState()
      return normalizeState(JSON.parse(raw) as Partial<PersistedState>)
    } catch {
      return buildDefaultState()
    }
  }

  function write(state: PersistedState): void {
    try {
      window.localStorage.setItem(WEB_STATE_KEY, JSON.stringify(normalizeState(state)))
    } catch {
      /* quota or serialization failure is non-fatal; backup key still applies */
    }
  }

  return {
    platform: 'web',
    isCloud: false,
    loadState: () => Promise.resolve(read()),
    saveState: (state) => {
      write(state)
      return Promise.resolve()
    },
    saveStateSync: (state) => {
      write(state)
    },
    openExternalLink: openExternalInBrowser
  }
}

let activeClient: PersistenceClient | null = null

/**
 * Resolve the persistence client for the current runtime.
 * Electron desktop uses the preload bridge; a configured web build uses the
 * Supabase-backed cloud client; everything else uses the local web client.
 * The result is memoized for the lifetime of the page.
 */
export function getPersistenceClient(): PersistenceClient {
  if (activeClient) return activeClient
  const bridge = typeof window !== 'undefined' ? window.investAlbum : undefined
  if (bridge) {
    activeClient = createElectronClient(bridge)
  } else if (isSupabaseConfigured) {
    activeClient = createCloudClient()
  } else {
    activeClient = createWebClient()
  }
  return activeClient
}
